'use client'

import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { animate, useMotionValue } from 'framer-motion'

const delimiter = ''

interface AnimatedTextCompletionProps {
  text: string
  isLast: boolean
  isStreaming: boolean
}

export function useAnimatedText({ text, isStreaming }: { text: string; isStreaming: boolean }) {
  const animatedCursor = useMotionValue(0)

  const [cursor, setCursor] = useState(0)
  const [prevText, setPrevText] = useState(text)
  const [isSameText, setIsSameText] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)

  if (prevText !== text) {
    setPrevText(text)
    setIsSameText(text.startsWith(prevText))

    if (!text.startsWith(prevText)) {
      setCursor(0)
    }
  }

  useEffect(() => {
    if (!isSameText) {
      animatedCursor.jump(0)
    }

    setIsAnimating(true)

    const controls = animate(animatedCursor, text.split(delimiter).length, {
      duration: 0.5,
      ease: 'easeOut',
      onUpdate(latest) {
        setCursor(Math.floor(latest))
      },
      onComplete() {
        setIsAnimating(false)
      },
    })

    return () => controls.stop()
  }, [animatedCursor, isSameText, text, isStreaming])

  return {
    animatedText: text.split(delimiter).slice(0, cursor).join(delimiter),
    isAnimating,
  }
}

export function AnimatedTextCompletion({ text, isLast, isStreaming }: AnimatedTextCompletionProps) {
  const { animatedText } = useAnimatedText({ text, isStreaming })

  const displayText = isLast && isStreaming ? animatedText : text

  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactMarkdown>{displayText}</ReactMarkdown>
    </div>
  )
}

interface AnimatedHeadlineProps {
  text: string
  className?: string
}

export function AnimatedHeadline({ text, className }: AnimatedHeadlineProps) {
  const { animatedText, isAnimating } = useAnimatedText({ text, isStreaming: true })

  return (
    <span className={className}>
      {animatedText}
      {isAnimating && <span className="animate-pulse">|</span>}
    </span>
  )
}
